import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { Swiper, SwiperSlide } from "swiper/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import { FreeMode, Navigation, Thumbs, Pagination } from "swiper";

import styles from "./Gallery.module.scss";
import FallbackImage from "../fallback-image";

export default function Gallery({ photos, category, root_subtype, lineage }) {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [isSwiperReady, setIsSwiperReady] = useState(false); // Track if Swiper is initialized
  const mainSwiperRef = useRef(null);
  const router = useRouter();

  // Reset Swiper to the first slide when the router query changes
  useEffect(() => {
    if (isSwiperReady && mainSwiperRef.current) {
      try {
        mainSwiperRef.current.slideTo(0);
      } catch (error) {
        console.error("Failed to reset Swiper position:", error);
      }
    }
  }, [router.query, isSwiperReady]);

  const renderFallbackImage = () => (
    <div className={styles.gallery}>
      <div className={styles.imageContainer}>
        <FallbackImage
          src={null}
          category={category}
          root_subtype={root_subtype}
          lineage={lineage}
          alt="Fallback image"
          fill
          style={{ objectFit: "contain" }}
        />
      </div>
    </div>
  );

  // Handle cases with no photos
  if (!photos || photos.length === 0) {
    return renderFallbackImage();
  }

  // Handle cases with a single photo
  if (photos.length === 1) {
    const imageSrc = photos[0]?.urls?.original || null;

    return (
      <div className={styles.gallery}>
        <div className={styles.imageContainer}>
          <FallbackImage
            src={imageSrc}
            alt="Product image"
            fill
            category={category}
            root_subtype={root_subtype}
            lineage={lineage}
            style={{ objectFit: "contain" }}
          />
        </div>
      </div>
    );
  }

  // Handle multiple photos with Swiper
  return (
    <div className={styles.gallery}>
      <Swiper
        slidesPerView={"auto"}
        grabCursor={photos.length > 1}
        navigation={{
          prevEl: `#custom-prev`,
          nextEl: `#custom-next`,
        }}
        pagination={{
          clickable: true,
        }}
        thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
        modules={[FreeMode, Navigation, Thumbs, Pagination]}
        className={styles.main}
        onSwiper={(swiper) => {
          mainSwiperRef.current = swiper; // Assign the Swiper instance
          setIsSwiperReady(true); // Mark Swiper as ready
        }}
      >
        {photos.map((photo, index) => (
          <SwiperSlide key={photo.id || index} className={styles.slide}>
            <div className={styles.imageContainer}>
              <FallbackImage
                src={photo?.urls?.original || null}
                alt={`Product image ${index + 1}`}
                fill
                category={category}
                root_subtype={root_subtype}
                lineage={lineage}
                style={{
                  objectFit: "contain",
                  cursor: photos.length > 1 ? "grab" : "default",
                }}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Pagination element */}
      <div id="gallery-pagination" className={`${styles.bullets} swiper-pagination`}></div>

      {/* Custom Navigation Buttons */}
      <button id="custom-prev" className={styles.navButtonPrev}>
        <FontAwesomeIcon icon={faArrowLeft} />
      </button>
      <button id="custom-next" className={styles.navButtonNext}>
        <FontAwesomeIcon icon={faArrowRight} />
      </button>
    </div>
  );
}
